import './_orders.scss';
import './_orders.dark-mode.scss';
import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
} from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from 'react-notifications-component';
import { isNull, isObject } from 'lodash';
import qs from 'qs';
import axios from 'axios';

import { useLocation } from 'react-router-dom';
import * as orderConstants from './constants';

import {
  ordersFetching,
  ordersUpdateList,
  ordersPaginationFetching,
  ordersError,
  ordersErrorInit,
  ordersStatusCounts,
  ordersPagination,
  ordersConfig,
  ordersList,
} from '../../redux/actions/orders';
import { ordersSelector } from '../../redux/selectors/orders';
import { prepareTableString } from './helpers';
import { copyToClipboard, delay, getErrorMessage, pageWrapperScrollTop, updateRows } from '../../utils/helper';
import {
  bulkUpdateOrdersStatus,
  cancelRequestOrderUrl,
  disableRefillOrderUrl,
  getOrdersConfig,
  getOrdersListPagination,
  getOrdersStatusesCounts,
  getOrdersUrl,
  refillOrderUrl,
  resendOrdersUrl,
} from '../../services/endpoints/orders';
import { defaultNotificationOptions } from '../../utils/config';
import { useFilters } from '../../providers/Filters/useFilters';

import ModalsManager from './components/Modals/Manager';
import Loader from '../../components/Loaders/Loader';
import OrdersTable from './components/OrdersTable';
import PageFilters from './components/Filters'
import Statuses from './components/Statuses';
import Search from './components/Search';
import SellingAlert from './components/SellingAlert';
import AlertComponent from '../../components/Alert';
import { Flex } from '../../components/Layout';
import { FilterPagination } from '../../components/Filters';

const Orders = () => {
  const dispatch = useDispatch();

  const { filters } = useFilters()
  const location = useLocation();

  const {
    isFetching,
    paginationFetching,
    error,
    actionFetching,
    pagination,
    orders,
    access,
    config,
    bulk_actions,
  } = useSelector((state) => ordersSelector(state));

  const [init, setInit] = useState(false);
  const [values, setValues] = useState([]);
  const [modal, setModal] = useState({
    openModalKey: null,
    data: {},
  });

  const cancelListRef = useRef(null);
  const cancelConfigRef = useRef(null);
  const cancelPaginationRef = useRef(null);
  const cancelStatusCountsRef = useRef(null);
  const timeoutRef = useRef(null);

  const checkSelectedOrder = (id) => {
    if (id) {
      setValues((prevState) => prevState.filter((selectedId) => Number(selectedId) !== Number(id)))
    }
  }

  const onHideModals = () => {
    setModal((prevState) => ({
      ...prevState,
      openModalKey: null,
      data: {},
    }));
  };

  const fetchStatusesCounts = async (currentQueryFilters) => {
    try {
      if (cancelStatusCountsRef.current) {
        cancelStatusCountsRef.current.cancel('Request counts canceled');
      }

      const source = axios.CancelToken.source();
      cancelStatusCountsRef.current = source;

      const respStatusesCounts = await getOrdersStatusesCounts(currentQueryFilters, source.token);

      dispatch(ordersStatusCounts(respStatusesCounts.data))

      return respStatusesCounts.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Status counts',
        message,
      });
    }
  };

  const fetchPagination = async (currentQueryFilters) => {
    try {
      dispatch(ordersPaginationFetching(true))
      if (cancelPaginationRef.current) {
        cancelPaginationRef.current.cancel('Request pagination canceled');
      }

      const source = axios.CancelToken.source();
      cancelPaginationRef.current = source;

      const response = await getOrdersListPagination(currentQueryFilters, source.token);

      // Save store
      dispatch(ordersPagination(response.data.pagination))

      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        return;
      }
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Pagination',
        message,
      });
    } finally {
      dispatch(ordersPaginationFetching(false))
    }
  };

  const fetchConfig = async (currentQueryFilters) => {
    try {
      if (cancelConfigRef.current) {
        cancelConfigRef.current.cancel('Request config canceled');
      }

      const source = axios.CancelToken.source();
      cancelConfigRef.current = source;

      const respConfig = await getOrdersConfig(currentQueryFilters, source.token);

      dispatch(ordersConfig(respConfig.data))
      return respConfig.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  };

  const fetchList = async (currentQueryFilters) => {
    try {
      if (cancelListRef.current) {
        cancelListRef.current.cancel('Request fetch canceled');
      }

      const source = axios.CancelToken.source();
      cancelListRef.current = source;

      const response = await getOrdersUrl(currentQueryFilters, source.token);

      if (!isNull(response.data?.queue?.id)) {
        return new Promise((resolve, reject) => {
          const timeoutId = setTimeout(async () => {
            try {
              const result = fetchList({
                ...currentQueryFilters,
                queue_id: response.data.queue.id,
              });
              resolve(result);
            } catch (error) {
              reject(error);
            }
          }, response.data.queue.timeout);

          if (timeoutRef.current) clearTimeout(timeoutRef.current);
          timeoutRef.current = timeoutId;
        });
      }

      // Save store
      dispatch(ordersList(response.data))

      // Check need_pagination
      if (response.data.need_pagination) {
        fetchPagination(currentQueryFilters)
      } else {
        // Save store
        dispatch((ordersPagination(response.data.pagination)))
      }

      const statuses = response.data?.statuses || [];
      const isSkeleton = statuses.some((item) => item.skeleton === true);

      // Check skeleton statuses
      if (isSkeleton) {
        fetchStatusesCounts(currentQueryFilters)
      }

      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) throw error;
      throw error;
    }
  };

  const fetchData = async () => {
    let isCanceled = false;
    try {
      dispatch(ordersFetching(true))

      const cancelsRefs = [cancelConfigRef, cancelListRef, cancelPaginationRef, cancelStatusCountsRef]

      for (let i = 0; i < cancelsRefs.length; i++) {
        const cancelRef = cancelsRefs[i];
        if (cancelRef.current) {
          cancelRef.current.cancel('Request canceled');
          cancelRef.current = null;
        }
      }

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      await Promise.all([fetchList(filters.query), fetchConfig(filters.query)])
      dispatch(ordersError(false))
      return {}
    } catch (error) {
      if (axios.isCancel(error)) {
        isCanceled = true;
        return;
      }
      const errorMessage = getErrorMessage(error);

      if (init) {
        dispatch(ordersError(errorMessage))
      } else {
        if (cancelConfigRef.current) {
          cancelConfigRef.current.cancel();
        }
        if (cancelListRef.current) {
          cancelListRef.current.cancel();
        }
        dispatch(ordersErrorInit({
          error: errorMessage,
        }))
      }
    } finally {
      if (!isCanceled) {
        dispatch(ordersFetching(false))
      }
      setInit(true)
    }
  };

  const updateOrders = async (resp) => {
    /**
     * Точечное обновление строк
     */
    if (isObject(resp) && resp.hasOwnProperty('updated_rows') && resp.hasOwnProperty('deleted_rows')) {
      // 1 - Формируем новый список
      const newOrdersList = updateRows(orders, resp);
      if (!newOrdersList.length) {
        // Slave delay
        dispatch(ordersFetching(true))
        await delay(3000)
        fetchData(filters.query)
      } else {
        // 2 - Обновляем список в state
        dispatch(ordersUpdateList(newOrdersList))
        // 3 - Запрашиваем новые каунтеры для табов
        fetchStatusesCounts(filters.query)
      }
    } else {
      fetchData(filters.query)
    }
  }

  const onSubmitModal = async (resp) => {
    updateOrders(resp);

    // Убираем ID из checked
    modal?.data?.id ? checkSelectedOrder(modal.data.id) : setValues([]);

    onHideModals();
  };

  useEffect(() => {
    fetchData()
    setValues([]);
    document.title = 'Orders';

    return () => {
      if (cancelStatusCountsRef.current) {
        cancelStatusCountsRef.current.cancel();
      }
      if (cancelPaginationRef.current) {
        cancelPaginationRef.current.cancel();
      }
      if (cancelConfigRef.current) {
        cancelConfigRef.current.cancel();
      }
      if (cancelListRef.current) {
        cancelListRef.current.cancel();
      }
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, filters.query]);

  useEffect(() => {
    if (init && (filters.query.page || filters.query.page_size)) {
      pageWrapperScrollTop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.query.page, filters.query.page_size]);

  /**
   *
   * @name disableRefill
   * @param id - order id
   * @returns {Promise<void>}
   */
  const disableRefill = async (id) => {
    dispatch(ordersFetching(true))
    try {
      const response = await disableRefillOrderUrl(filters.query, id)
      checkSelectedOrder(id)
      return await updateOrders(response.data)
    } catch (error) {
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Disable refill',
        message,
      });
      dispatch(ordersFetching(false))
    }
  }

  /**
   * @name requestRefill
   * @param id - order id
   * @returns {Promise<void>}
   */
  const requestRefill = async (id) => {
    dispatch(ordersFetching(true))
    try {
      const response = await refillOrderUrl(filters.query, id)
      checkSelectedOrder(id)
      return await updateOrders(response.data)
    } catch (error) {
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Request refill',
        message,
      });
      dispatch(ordersFetching(false))
    }
  }

  /**
   * @name resendOrder
   * @param id - order id
   * @returns {Promise<void>}
   */
  const resendOrder = async (id) => {
    dispatch(ordersFetching(true))
    try {
      const response = await resendOrdersUrl(id, filters.query)
      checkSelectedOrder(id)
      return await updateOrders(response.data)
    } catch (error) {
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Resend order',
        message,
      });
      dispatch(ordersFetching(false))
    }
  }

  /**
   * @name massResendOrder
   * @returns {Promise<void>}
   */
  const massResendOrder = async () => {
    dispatch(ordersFetching(true))
    const queryParams = qs.parse(window.location.search.slice(1));
    try {
      const response = await bulkUpdateOrdersStatus(queryParams, {
        orders: values,
        status: orderConstants.RESEND,
      });
      await updateOrders(response.data);
      setValues([]);
      return {}
    } catch (error) {
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Mass resend order',
        message,
      });
      dispatch(ordersFetching(false))
    }
  }

  /**
   * @name requestCancel
   * @param id
   * @returns {Promise<void>}
   */
  const requestCancel = async (id) => {
    dispatch(ordersFetching(true))
    try {
      const response = await cancelRequestOrderUrl(id, filters.query)
      checkSelectedOrder()
      return await updateOrders(response.data)
    } catch (error) {
      const message = getErrorMessage(error);
      Store.addNotification({
        ...defaultNotificationOptions,
        title: 'Request cancel',
        message,
      });
      dispatch(ordersFetching(false))
    }
  }

  const handleSelect = (key, order, actionType) => {
    if (key !== undefined || key !== null) {
      const actionKey = `${key}`.toUpperCase();
      switch (actionKey) {
      case orderConstants.EDIT_LINK:
      case orderConstants.SET_PARTIAL:
      case orderConstants.UPDATE_REMAINS:
      case orderConstants.SET_REMAINS:
      case orderConstants.CANCEL_REASON:
      case orderConstants.CANCEL_AND_REFUND:
      case orderConstants.ORDER_DETAILS:
      case orderConstants.ORDER_INFO:
      case orderConstants.ORDER_DETAILS_SUPER_ADMIN:
      case orderConstants.UPDATE_FROM_PROVIDER:
      case orderConstants.MASS_CANCEL_AND_REFUND:
      case orderConstants.MASS_REQUEST_REFILL:
      case orderConstants.MASS_REQUEST_CANCEL:
      case orderConstants.MASS_UPDATE_FROM_PROVIDER:
      case orderConstants.SET_START_COUNT: {
        setModal((prevState) => ({
          ...prevState,
          openModalKey: key,
          data: {
            ...order,
          },
        }));
        break;
      }
      case orderConstants.STATUS_KEY_ERROR:
      case orderConstants.STATUS_KEY_FAIL:
      case orderConstants.STATUS_KEY_PROCESSING:
      case orderConstants.STATUS_KEY_CANCELED:
      case orderConstants.STATUS_KEY_PARTIAL:
      case orderConstants.STATUS_KEY_COMPLETED:
      case orderConstants.STATUS_KEY_INPROGRESS:
      case orderConstants.STATUS_KEY_AWAITING:
      case orderConstants.STATUS_KEY_PENDING: {
        setModal((prevState) => ({
          ...prevState,
          openModalKey: actionType === 'mass' ? orderConstants.MASS_CHANGE_STATUS : orderConstants.CHANGE_STATUS,
          data: {
            ...order,
            changeStatusTo: actionKey,
          },
        }));
        break;
      }
      case orderConstants.REQUEST_CANCEL:
        requestCancel(order.id);
        break;
      case orderConstants.RESEND_ORDER: {
        const isOperationTimeOut = order?.order_fail_reason === 8 && config?.access?.details;
        /**
         * Если ошибка "No response from provider"
         * Нужна модалка подтверждения
         */
        if (isOperationTimeOut) {
          setModal((prevState) => ({
            ...prevState,
            openModalKey: orderConstants.RESEND_ORDER_CONFIRM,
            data: {
              ...order,
            },
          }));
          return;
        }
        resendOrder(order.id);
        break;
      }
      case orderConstants.REQUEST_REFILL:
        requestRefill(order.id);
        break;
      case orderConstants.DISABLE_REFILL:
        disableRefill(order.id);
        break;
      case orderConstants.MASS_RESEND: {
        const ordersWithOperationTimeOut = orders.filter((order) => values.includes(order?.id) && order?.order_fail_reason === 8);
        const isOperationTimeOut = ordersWithOperationTimeOut.length && config?.access?.details;
        /**
         * Если ошибка "No response from provider"
         * Нужна модалка подтверждения
         */
        if (isOperationTimeOut) {
          setModal((prevState) => ({
            ...prevState,
            openModalKey: orderConstants.MASS_RESEND_CONFIRM,
            data: {},
          }));
          return;
        }
        massResendOrder();
        break;
      }
      case orderConstants.COPY_ORDER_ID:
        return copyToClipboard(values);
      case orderConstants.COPY_EXTERNAL_ID:
        const externalIds = orders
          .filter((item) => values.includes(String(item.id)))
          .filter((item) => item.external_id)
          .map((item) => item.external_id);
        return copyToClipboard(externalIds);
      case orderConstants.COPY_ALL_COLUMN_DATA:
      case orderConstants.COPY_ALL_COLUMN_DATA_WITH_PROVIDER: {
        const formated = prepareTableString(orders, values, {
          ...access,
          ...config?.fields,
          showProvider: actionKey === orderConstants.COPY_ALL_COLUMN_DATA_WITH_PROVIDER,
        });
        return copyToClipboard(formated.join('\r\n'));
      }
      case orderConstants.COPY_LINK: {
        const formated = orders
          .filter((item) => values.includes(String(item.id)))
          .filter((item) => item.link)
          .map((item) => item.link);
        return copyToClipboard(formated.join('\r\n'));
      }
      default:
        return null;
      }
    }
  };

  if (!init) {
    return <Loader />
  }

  return (
    <Grid fluid className="container-loader">
      <ModalsManager
        modal={modal}
        values={values}
        access={access}
        config={config}
        onHide={onHideModals}
        onSubmit={onSubmitModal}
      />
      {config?.access?.list
        ? (
          <>
            <Flex
              direction={{ default: 'column', lg: 'row' }}
              wrap={{ lg: 'wrap' }}
              mb={{ lg: 16 }}
              gap={16}
            >
              <Flex
                grow={{ lg: 1 }}
                basis={{ lg: 0 }}
                order={1}
              >
                <Statuses />
              </Flex>
              <Flex
                shrink={0}
                order={{ default: 3, lg: 2 }}
                ml={{ lg: 'auto' }}
                mb={{ default: 16, lg: 0 }}
              >
                <Search />
              </Flex>
              <Flex
                grow={{ lg: 1 }}
                order={{ default: 2, lg: 3 }}
                width="100%"
              >
                <PageFilters />
              </Flex>
            </Flex>
            <SellingAlert />
          </>
        ) : null}
      {error ? <AlertComponent text={error} type="danger" /> : null}
      {config?.access?.list
        ? (
          <>
            <OrdersTable
              handleSelect={handleSelect}
              values={values}
              setValues={setValues}
              config={config}
              access={access}
              orders={orders}
              actionFetching={actionFetching}
              bulkActions={bulk_actions}
              isTableLoading={isFetching}
            />
            <FilterPagination
              showPageSize
              preLoader={paginationFetching || isFetching}
              pages={pagination?.pages}
              currentPage={pagination?.current_page}
              itemsCount={pagination?.total_count}
              pageSize={pagination?.page_size}
              pagesLimit={pagination?.pages < 10 ? pagination?.pages : 10}
            />
          </>
        ) : null}
    </Grid>
  );
};

export default Orders;
